import {
  CreateIndividualIncomesDto,
  IncomeType,
  IndividualIncome,
  IncomesDto,
  OtherIncomeDto,
  PayeIncome,
} from 'api/btlApplicationForm/types';

const COMMON_INCOME_FIELDS = ['companyName', 'occupation'] as const;

const INCOME_FIELDS_BY_TYPE: Record<IncomeType, readonly string[]> = {
  [IncomeType.PAYE_EMPLOYEE]: [
    ...COMMON_INCOME_FIELDS,
    'timeInJob',
    'basicIncome',
    'employersAllowance',
    'bonus',
    'commission',
    'overtime',
  ],
  [IncomeType.SALARIED_DIRECTOR]: [
    ...COMMON_INCOME_FIELDS,
    'timeTrading',
    'percentageShareholding',
    'salaryMostRecentYear',
    'salaryPriorYear',
    'dividendsMostRecentYear',
    'dividendsPriorYear',
  ],
  [IncomeType.SOLE_TRADER]: [
    ...COMMON_INCOME_FIELDS,
    'timeTrading',
    'netTaxableIncomeMostRecentYear',
    'netTaxableIncomePriorYear',
  ],
  [IncomeType.CONTRACTOR]: [
    ...COMMON_INCOME_FIELDS,
    'timeTrading',
    'timeRemainingOnContract',
    'netTaxableIncomeMostRecentYear',
    'netTaxableIncomePriorYear',
  ],
  [IncomeType.PARTNERSHIP]: [
    ...COMMON_INCOME_FIELDS,
    'timeTrading',
    'percentageShareholding',
    'netTaxableIncomeMostRecentYear',
    'netTaxableIncomePriorYear',
  ],
};

function omitId<T extends { id?: string }>(value: T): Omit<T, 'id'> {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { id, ...rest } = value;
  return rest;
}

function resolveEmploymentType(income: Record<string, unknown>, caseIncome?: IndividualIncome): IncomeType | undefined {
  if (income.employmentType) {
    return income.employmentType as IncomeType;
  }

  if (income.type) {
    return income.type as IncomeType;
  }

  return resolveEmploymentTypeFromNested(caseIncome) ?? resolveEmploymentTypeFromNested(income as IndividualIncome);
}

function resolveEmploymentTypeFromNested(income?: IndividualIncome): IncomeType | undefined {
  if (!income) {
    return undefined;
  }

  if (income.payeIncome) {
    return IncomeType.PAYE_EMPLOYEE;
  }

  if (income.soleTraderIncome) {
    return IncomeType.SOLE_TRADER;
  }

  if (income.contractorIncome) {
    return IncomeType.CONTRACTOR;
  }

  if (income.partnershipIncome) {
    return IncomeType.PARTNERSHIP;
  }

  if (income.salariedDirectorIncome) {
    return IncomeType.SALARIED_DIRECTOR;
  }

  return undefined;
}

function stripNestedIncomeFields(income: Record<string, unknown>): Record<string, unknown> {
  const rest = { ...income };

  delete rest.payeIncome;
  delete rest.soleTraderIncome;
  delete rest.contractorIncome;
  delete rest.partnershipIncome;
  delete rest.salariedDirectorIncome;
  delete rest.type;
  delete rest.employmentType;

  return rest;
}

function flattenNestedIncomeByType(
  employmentType: IncomeType | undefined,
  caseIncome?: IndividualIncome,
  income?: IndividualIncome,
): Record<string, unknown> {
  const source = caseIncome ?? income;

  if (!source || !employmentType) {
    return {};
  }

  switch (employmentType) {
    case IncomeType.PAYE_EMPLOYEE:
      return source.payeIncome ? omitId(source.payeIncome) : {};
    case IncomeType.SALARIED_DIRECTOR:
      return source.salariedDirectorIncome ? omitId(source.salariedDirectorIncome) : {};
    case IncomeType.SOLE_TRADER:
      return source.soleTraderIncome ? omitId(source.soleTraderIncome) : {};
    case IncomeType.CONTRACTOR:
      return source.contractorIncome ? omitId(source.contractorIncome) : {};
    case IncomeType.PARTNERSHIP:
      return source.partnershipIncome ? omitId(source.partnershipIncome) : {};
    default:
      return {};
  }
}

export function normalizeIndividualIncomesForForm(
  incomes: Array<IncomesDto | IndividualIncome> = [],
  caseIncomes: IndividualIncome[] = [],
): Record<string, unknown>[] {
  const caseIncomesById = new Map(caseIncomes.filter(income => income.id).map(income => [income.id, income]));

  return incomes.map((income, index) => {
    const caseIncome = income.id ? caseIncomesById.get(income.id) : caseIncomes[index];
    const incomeRecord = income as Record<string, unknown>;
    const employmentType = resolveEmploymentType(incomeRecord, caseIncome);
    const flatFromNested = flattenNestedIncomeByType(employmentType, caseIncome, income as IndividualIncome);
    const flatFromRecord = stripNestedIncomeFields(incomeRecord);

    const normalized = {
      ...flatFromNested,
      ...flatFromRecord,
      employmentType,
    };

    if (employmentType === IncomeType.PAYE_EMPLOYEE && caseIncome?.payeIncome) {
      return {
        ...normalized,
        ...omitId(caseIncome.payeIncome as PayeIncome),
      };
    }

    return normalized;
  });
}

export function mapIndividualIncomesToFormValues(
  incomesData?: CreateIndividualIncomesDto | null,
  caseIncomes: IndividualIncome[] = [],
): Record<string, unknown> {
  const sourceIncomes = incomesData?.incomes?.length ? incomesData.incomes : caseIncomes;

  return {
    incomes: normalizeIndividualIncomesForForm(sourceIncomes ?? [], caseIncomes),
    otherIncome: incomesData?.otherIncome,
  };
}

function hasEmploymentTypeChanged(formIncome: Record<string, unknown>, caseIncome?: IndividualIncome): boolean {
  const nextEmploymentType = resolveEmploymentType(formIncome, caseIncome);
  const originalEmploymentType = resolveEmploymentTypeFromNested(caseIncome);

  if (!caseIncome || !nextEmploymentType || !originalEmploymentType) {
    return false;
  }

  return nextEmploymentType !== originalEmploymentType;
}

function pickIncomeFieldsForType(
  formIncome: Record<string, unknown>,
  employmentType: IncomeType,
): Record<string, unknown> {
  return INCOME_FIELDS_BY_TYPE[employmentType].reduce<Record<string, unknown>>((fields, field) => {
    if (formIncome[field] !== undefined) {
      fields[field] = formIncome[field];
    }

    return fields;
  }, {});
}

export function mapFormIncomesToDto(
  formIncomes: Record<string, unknown>[] = [],
  caseIncomes: IndividualIncome[] = [],
): IncomesDto[] {
  const caseIncomesById = new Map(caseIncomes.filter(income => income.id).map(income => [income.id, income]));

  return formIncomes
    .map((formIncome, index) => {
      const caseIncome = formIncome.id ? caseIncomesById.get(formIncome.id as string) : caseIncomes[index];
      const employmentType = resolveEmploymentType(formIncome, caseIncome);

      if (!employmentType) {
        return null;
      }

      const shouldOmitId = hasEmploymentTypeChanged(formIncome, caseIncome);
      const typeSpecificFields = pickIncomeFieldsForType(formIncome, employmentType);

      return {
        ...(shouldOmitId || !formIncome.id ? {} : { id: formIncome.id as string }),
        employmentType,
        isPrimary: index === 0,
        ...typeSpecificFields,
      } as IncomesDto;
    })
    .filter((income): income is IncomesDto => income !== null);
}

export function mapFormIncomesPayloadToDto(
  values: { incomes?: Record<string, unknown>[]; otherIncome?: OtherIncomeDto },
  caseIncomes: IndividualIncome[] = [],
): CreateIndividualIncomesDto {
  return {
    incomes: mapFormIncomesToDto(values.incomes ?? [], caseIncomes),
    otherIncome: values.otherIncome,
  };
}
